import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { CadifyError, ErrorTypes, validateFile, validateGeometry } from './errors.js';

// Supported file formats
export const SUPPORTED_FORMATS = {
  stl: { name: 'STL', extensions: ['stl'], loader: 'STLLoader' },
  obj: { name: 'OBJ', extensions: ['obj'], loader: 'OBJLoader' },
  gltf: { name: 'GLTF/GLB', extensions: ['gltf', 'glb'], loader: 'GLTFLoader' },
  ply: { name: 'PLY', extensions: ['ply'], loader: 'PLYLoader' },
  fbx: { name: 'FBX', extensions: ['fbx'], loader: 'FBXLoader' },
  dae: { name: 'Collada', extensions: ['dae'], loader: 'ColladaLoader' },
  svg: { name: 'SVG', extensions: ['svg'], loader: 'SVGLoader' }
};

export const getAllSupportedExtensions = () => {
  return Object.values(SUPPORTED_FORMATS).flatMap(f => f.extensions);
};

// Load STL file
export const loadSTL = (arrayBuffer) => {
  return new Promise((resolve, reject) => {
    try {
      const loader = new STLLoader();
      const geometry = loader.parse(arrayBuffer);
      validateGeometry(geometry);
      geometry.computeVertexNormals();
      geometry.computeBoundingBox();
      
      const material = new THREE.MeshStandardMaterial({
        color: 0x9bbcff,
        metalness: 0.1,
        roughness: 0.6,
        side: THREE.DoubleSide
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      resolve({ mesh, geometry, type: 'stl' });
    } catch (error) {
      reject(new CadifyError('Failed to parse STL file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Load OBJ file
export const loadOBJ = (text) => {
  return new Promise((resolve, reject) => {
    try {
      const loader = new OBJLoader();
      const object = loader.parse(text);
      
      if (!object || object.children.length === 0) {
        throw new Error('No geometry found in OBJ file');
      }
      
      // Apply default material if needed
      object.traverse((child) => {
        if (child.isMesh) {
          if (!child.material || child.material.length === 0) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0x9bbcff,
              metalness: 0.1,
              roughness: 0.6,
              side: THREE.DoubleSide
            });
          }
          child.castShadow = true;
          child.receiveShadow = true;
          
          if (child.geometry) {
            child.geometry.computeVertexNormals();
            child.geometry.computeBoundingBox();
          }
        }
      });
      
      resolve({ mesh: object, type: 'obj' });
    } catch (error) {
      reject(new CadifyError('Failed to parse OBJ file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Load GLTF/GLB file
export const loadGLTF = (arrayBuffer, isGLB = false) => {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    
    const onLoad = (gltf) => {
      try {
        const scene = gltf.scene;
        
        if (!scene || scene.children.length === 0) {
          throw new Error('No geometry found in GLTF file');
        }
        
        scene.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            if (child.geometry) {
              child.geometry.computeVertexNormals();
              child.geometry.computeBoundingBox();
            }
          }
        });
        
        resolve({ mesh: scene, type: 'gltf', animations: gltf.animations });
      } catch (error) {
        reject(new CadifyError('Failed to process GLTF data', ErrorTypes.FILE_PARSE, error));
      }
    };
    
    const onError = (error) => {
      reject(new CadifyError('Failed to load GLTF file', ErrorTypes.FILE_LOAD, error));
    };
    
    if (isGLB) {
      loader.parse(arrayBuffer, '', onLoad, onError);
    } else {
      const text = new TextDecoder().decode(arrayBuffer);
      const blob = new Blob([text], { type: 'model/gltf+json' });
      const url = URL.createObjectURL(blob);
      loader.load(url, onLoad, undefined, onError);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  });
};

// Load PLY file
export const loadPLY = (arrayBuffer) => {
  return new Promise((resolve, reject) => {
    try {
      const loader = new PLYLoader();
      const geometry = loader.parse(arrayBuffer);
      validateGeometry(geometry);
      geometry.computeVertexNormals();
      geometry.computeBoundingBox();
      
      const material = new THREE.MeshStandardMaterial({
        color: 0x9bbcff,
        metalness: 0.1,
        roughness: 0.6,
        vertexColors: geometry.attributes.color ? true : false,
        side: THREE.DoubleSide
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      
      resolve({ mesh, geometry, type: 'ply' });
    } catch (error) {
      reject(new CadifyError('Failed to parse PLY file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Load FBX file
export const loadFBX = (arrayBuffer) => {
  return new Promise((resolve, reject) => {
    try {
      const loader = new FBXLoader();
      const object = loader.parse(arrayBuffer, '');
      
      if (!object) {
        throw new Error('No geometry found in FBX file');
      }
      
      object.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          
          if (child.geometry) {
            child.geometry.computeVertexNormals();
            child.geometry.computeBoundingBox();
          }
        }
      });
      
      resolve({ mesh: object, type: 'fbx' });
    } catch (error) {
      reject(new CadifyError('Failed to parse FBX file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Load Collada file
export const loadCollada = (text) => {
  return new Promise((resolve, reject) => {
    const loader = new ColladaLoader();
    
    try {
      const collada = loader.parse(text);
      const scene = collada.scene;
      
      if (!scene) {
        throw new Error('No geometry found in Collada file');
      }
      
      scene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          
          if (child.geometry) {
            child.geometry.computeVertexNormals();
            child.geometry.computeBoundingBox();
          }
        }
      });
      
      resolve({ mesh: scene, type: 'dae' });
    } catch (error) {
      reject(new CadifyError('Failed to parse Collada file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Load SVG file
export const loadSVG = (text, depth = 2, scale = 1) => {
  return new Promise((resolve, reject) => {
    try {
      const loader = new SVGLoader();
      const data = loader.parse(text);
      const paths = data.paths;
      
      if (!paths || paths.length === 0) {
        throw new Error('No paths found in SVG file');
      }
      
      const group = new THREE.Group();
      
      for (const path of paths) {
        const shapes = SVGLoader.createShapes(path);
        for (const shape of shapes) {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: Math.max(0, depth),
            bevelEnabled: false,
            curveSegments: 24
          });
          
          const material = new THREE.MeshStandardMaterial({
            color: path.color || 0xcccccc,
            metalness: 0.05,
            roughness: 0.85,
            side: THREE.DoubleSide
          });
          
          const mesh = new THREE.Mesh(geometry, material);
          group.add(mesh);
        }
      }
      
      group.scale.y *= -1;
      group.scale.multiplyScalar(scale);
      
      resolve({ mesh: group, paths, type: 'svg' });
    } catch (error) {
      reject(new CadifyError('Failed to parse SVG file', ErrorTypes.FILE_PARSE, error));
    }
  });
};

// Main loader function
export const loadFile = async (file, options = {}) => {
  const ext = file.name.split('.').pop().toLowerCase();
  validateFile(file, getAllSupportedExtensions());
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const data = event.target.result;
        let result;
        
        switch (ext) {
          case 'stl':
            result = await loadSTL(data);
            break;
          case 'obj':
            result = await loadOBJ(new TextDecoder().decode(data));
            break;
          case 'gltf':
            result = await loadGLTF(data, false);
            break;
          case 'glb':
            result = await loadGLTF(data, true);
            break;
          case 'ply':
            result = await loadPLY(data);
            break;
          case 'fbx':
            result = await loadFBX(data);
            break;
          case 'dae':
            result = await loadCollada(new TextDecoder().decode(data));
            break;
          case 'svg':
            result = await loadSVG(new TextDecoder().decode(data), options.svgDepth, options.svgScale);
            break;
          default:
            throw new CadifyError(`Unsupported format: .${ext}`, ErrorTypes.VALIDATION);
        }
        
        resolve({ ...result, fileName: file.name });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new CadifyError('Failed to read file', ErrorTypes.FILE_LOAD));
    };
    
    if (ext === 'obj' || ext === 'svg' || ext === 'dae' || ext === 'gltf') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

